<dialog
    #reviewDrawer
    class="review-questions-drawer"
    aria-labelledby="review-questions-drawer-title">
    <div class="flex h-full justify-end" (click)="onBackdrop($event)">
        <div class="flex h-full w-full max-w-xl flex-col bg-card text-foreground shadow-xl"
            (click)="$event.stopPropagation()">
            <header class="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-5 sm:px-7">
                <div class="min-w-0">
                    <h2 id="review-questions-drawer-title" class="m-0 text-3xl font-bold tracking-tight text-foreground">
                        Context required
                    </h2>
                    <p class="m-0 mt-1 text-sm text-muted-foreground">{{ briefTitle() }}</p>
                </div>
                <button type="button" (click)="close()" class="btn-shout-fab-secondary shrink-0" aria-label="Close">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-4" aria-hidden="true">
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                    </svg>
                </button>
            </header>

            <div class="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-5 sm:px-7">
                <p class="m-0 text-sm leading-relaxed text-muted-foreground">
                    A few requirements on this brief were generalised from specific examples, so they're missing the
                    context we need to review accurately. We'll fix them one by one.
                </p>

                @if (current(); as question) {
                <div class="flex items-center justify-between gap-3">
                    <p class="m-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Requirement {{ stepIndex() + 1 }} of {{ steps().length }}
                    </p>
                    <div class="flex items-center gap-2">
                        @for (step of steps(); track step.id; let i = $index) {
                        <button type="button" (click)="goTo(i)" [class]="dotClass(i)"
                            [attr.aria-label]="'Requirement ' + (i + 1)"
                            [attr.aria-current]="i === stepIndex() ? 'step' : null"></button>
                        }
                    </div>
                </div>

                <div class="flex flex-col gap-3 border-b border-border pb-5">
                    @if (question.type === 'do') {
                    <p
                        class="m-0 inline-flex w-fit items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800 dark:bg-green-500/15 dark:text-green-300">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
                            class="size-3.5" aria-hidden="true">
                            <path d="M20 6 9 17l-5-5" />
                        </svg>
                        Do
                    </p>
                    } @else {
                    <p
                        class="m-0 inline-flex w-fit items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800 dark:bg-red-500/15 dark:text-red-300">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
                            class="size-3.5" aria-hidden="true">
                            <path d="M18 6 6 18" />
                            <path d="m6 6 12 12" />
                        </svg>
                        Don't
                    </p>
                    }
                    <h3 class="m-0 text-2xl font-bold leading-snug text-foreground">“{{ question.statement }}”</h3>
                    <p class="m-0 text-sm leading-relaxed text-muted-foreground">
                        Add the context we should use when reviewing against it.
                        @if (loadState() === 'ready' && currentOptions().length === 0) {
                        Describe it in your own words.
                        } @else {
                        Pick all that apply.
                        }
                    </p>
                </div>

                @if (loadState() === 'loading') {
                <p class="m-0 text-sm text-muted-foreground">Loading context options…</p>
                } @else if (loadState() === 'error') {
                <p class="m-0 text-sm text-red-700 dark:text-red-300">Couldn't load context options for this requirement.</p>
                } @else if (currentOptions().length) {
                <ul class="m-0 flex list-none flex-col divide-y divide-border p-0">
                    @for (option of currentOptions(); track option) {
                    <li>
                        <button type="button" (click)="togglePick(option)"
                            class="flex w-full items-start gap-3 px-1 py-3 text-left hover:bg-muted/60"
                            [attr.aria-pressed]="isPicked(option)">
                            <span
                                [class]="isPicked(option)
                                    ? 'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded border border-fuchsia-600 bg-fuchsia-600 text-white'
                                    : 'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded border border-border bg-card'"
                                aria-hidden="true">
                                @if (isPicked(option)) {
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"
                                    stroke-linejoin="round" class="size-3.5">
                                    <path d="M20 6 9 17l-5-5" />
                                </svg>
                                }
                            </span>
                            <p class="m-0 text-sm leading-relaxed text-foreground">{{ option }}</p>
                        </button>
                    </li>
                    }
                </ul>
                } @else {
                <textarea rows="5" class="w-full resize-y rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground"
                    [value]="currentDraft().text" (input)="onTextInput($event)"
                    placeholder="Add the details we should check for"
                    [attr.aria-label]="'Context for ' + question.statement"></textarea>
                }
                }
            </div>

            <footer class="flex shrink-0 items-center justify-between gap-3 border-t border-border px-5 py-4 sm:px-7">
                <button type="button" (click)="back()" class="btn-shout btn-shout-sm btn-shout-secondary"
                    [disabled]="stepIndex() === 0 || !canAdvance()">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-4" aria-hidden="true">
                        <path d="m12 19-7-7 7-7" />
                        <path d="M19 12H5" />
                    </svg>
                    Back
                </button>
                <button type="button" (click)="next()" class="btn-shout btn-shout-sm btn-shout-primary"
                    [disabled]="!canAdvance()">
                    {{ saveState() === 'saving' ? 'Saving…' : (isLast() ? 'Save context' : 'Next requirement') }}
                    @if (isLast()) {
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-4" aria-hidden="true">
                        <path d="M20 6 9 17l-5-5" />
                    </svg>
                    } @else {
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-4" aria-hidden="true">
                        <path d="M5 12h14" />
                        <path d="m12 5 7 7-7 7" />
                    </svg>
                    }
                </button>
            </footer>
            @if (saveState() === 'error') {
            <p class="m-0 px-5 pb-4 text-sm text-red-700 sm:px-7 dark:text-red-300">
                Couldn't save this context. Try again.
            </p>
            }
        </div>
    </div>
</dialog>
